import * as React from 'react';
import CollapsibleArea from '../../components/common/CollapsibleArea';
import PTOAdminTable from './PTOAdminTable';
import { PTOEntryType, PTOPlanType } from './types';
import Loader from 'src/components/common/Loader';
import './PTOAdminTableWrapper.css';

interface IProps {
    currentProfitCenter: { id: string, displayname: string, name: string } | null;
    fromToDate: { from: string, to: string };
    includeInactive: boolean;
    reference: { states: Array<any>, profitCenters: Array<any>, departments: Array<any>, ptoPlans: Array<PTOPlanType> };
    ptoData: {
        data: Array<PTOEntryType>;
        ledgerEntryTypes: Array<any>;
        ledgerStatusTypes: Array<any>;
    };
    updateTable: any;
    searchValue: string;
    showOnlyPending: boolean;
    displayZeros: boolean;
    setDisplayZeros: (displayZeros: boolean) => void;
    expandDetail: boolean;
    setExpandDetail: (expandDetail: boolean) => void;
    expandCity: boolean;
    setExpandCity: (expandCity: boolean) => void;
}

const PTOAdminTableWrapper: React.FC<IProps> = (props: IProps) => {
    interface Totals {
        balance: number;
        pending: number;
        used: number;
    }
    const [total, setTotal] = React.useState({} as Totals);
    const [modifiedPTO, setModifiedPTO] = React.useState({
        data: [],
        ledgerEntryTypes: [],
        ledgerStatusTypes: []
    } as {
        data: Array<PTOEntryType>;
        ledgerEntryTypes: Array<any>;
        ledgerStatusTypes: Array<any>;
    });
    const [employeeCount, setEmployeeCount] = React.useState<number>(-1);
    const [filteredEmployeeCount, setFilteredEmployeeCount] = React.useState<number>(-1);

    const { currentProfitCenter, fromToDate, includeInactive, reference, ptoData, showOnlyPending, displayZeros, expandDetail, setExpandDetail, expandCity } = props;
    const tableRef: any = React.useRef();

    const updateTotal = () => {
        tableRef.current?.toggleLoading(true);
        let newTotals: Totals = { balance: 0, pending: 0, used: 0 } as Totals;
        let filteredEntries: Array<PTOEntryType> = ptoData.data.filter((entry: PTOEntryType) => {
            if (showOnlyPending && entry.PendingHours === 0) return false;
            return props.searchValue ? entry.DisplayName.toLowerCase().includes(props.searchValue.toLowerCase()) : true
        });
        let unfilteredEntries: Array<PTOEntryType> = ptoData.data;
        setEmployeeCount(unfilteredEntries.length);
        if (ptoData) {
            filteredEntries.forEach((entry: PTOEntryType) => {
                //@ts-ignore
                newTotals["balance"] += Math.round(entry.CurrentBalance) || 0;
                newTotals["pending"] += Math.abs(Math.round(entry.PendingHours)) || 0;
                newTotals["used"] += Math.abs(Math.round(entry.UsedYTD)) || 0;
            });
        }
        setTotal(newTotals);

        setModifiedPTO({ ...ptoData, data: filteredEntries });
        tableRef.current?.toggleLoading(false);
    }



    const triggerTableUpdate = () => {
        props.updateTable(currentProfitCenter?.id);
    }

    React.useEffect(() => {
        updateTotal();
        //r-18-lint-cm
        //eslint-disable-next-line
    }, [ptoData, props.searchValue, props.showOnlyPending]);


    if (tableRef.current?.toggleLoading === true) {
        return (
            <Loader title="Loading Employee Information..." />
        )
    }

    return (
        <CollapsibleArea
            Toggle={(({ onClick, open }: any) => (
                // conditional rendering the bar color and background based on whether it's selected and or has requested hours pending
                <div className={
                    total.pending > 0 && open ? `city-cell city-selected warning d-flex  flex-grow-1 p-1 action-text align-items-center justify-content-between` :
                        total.pending > 0 && !open ? `city-cell warning d-flex  flex-grow-1 p-1 action-text align-items-center justify-content-between` :
                            total.pending <= 0 && open ? `city-cell city-selected d-flex  flex-grow-1 p-1 action-text align-items-center justify-content-between` :
                                `city-cell d-flex  flex-grow-1 p-1 action-text align-items-center justify-content-between`
                } onClick={onClick}>
                    <div className='city-label d-inline-flex'>
                        <h5 className={`${open && "underline"}`}>{currentProfitCenter?.name}</h5>
                    </div>
                    <div className="flex-shrink-1">
                        <div className='employee-showing-count mr-5 d-inline-flex city-divs city-divs-vertical' >
                            {
                                filteredEmployeeCount === 0 ?
                                    <div className='d-inline-flex city-divs justify-content-end'>
                                        <strong className='requested-text '>No Pending Requests</strong>
                                    </div> :
                                    (filteredEmployeeCount !== -1 && open) &&
                                    <div className='d-inline-flex city-divs '>
                                        <strong className='requested-text '>Showing {filteredEmployeeCount} of {employeeCount} employees </strong>
                                    </div>
                            }
                        </div>

                        <div className={total.balance === 0 && !displayZeros ? 'grayed balance-spacing d-inline-flex city-divs city-divs-vertical ml-5' : 'balance-spacing ml-5 d-inline-flex  city-divs city-divs-vertical'}>
                            <strong>Balance: </strong>

                            <span className=' ml-3 mr-5'>{total.balance}</span>
                        </div>
                        <div className={total.used === 0 && !displayZeros ? 'grayed used-spacing d-inline-flex city-divs city-divs-vertical' : 'used-spacing d-inline-flex  city-divs city-divs-vertical'}>
                            <strong className="ml-5">Used: </strong>
                            {/* cgm 042022 =>> bug where NaN was returned fix by casting to a string */}
                            <span className='ml-3 mr-5'>{total.used > 0 ? String(total.used) : String(-total.used)}</span>
                        </div>
                        <div className={total.pending === 0 && !displayZeros ? 'grayed requested-spacing d-inline-flex city-divs' : 'requested-spacing d-inline-flex city-divs'}>
                            <strong className='requested-text mr-3 ml-5'>Requested: </strong>
                            <span className={total.pending > 0 ? "requested-number text-warning" : "requested-zero"}>{total.pending > 0 ? String(total.pending) : String(-total.pending)}</span>
                        </div>
                        <div className='d-inline-flex'>

                        </div>

                    </div>

                    <i className="material-icons mr-1">{open ? "expand_less" : "expand_more"}</i>
                </div>
            ))}
            startOpen={expandCity}
        >
            <PTOAdminTable
                currentProfitCenter={currentProfitCenter}
                fromToDate={fromToDate}
                includeInactive={includeInactive}
                reference={reference}
                updateTotal={updateTotal}
                updateTable={triggerTableUpdate}
                ptoData={modifiedPTO}
                startVertical={false}
                displayZeros={props.displayZeros}
                setDisplayZeros={props.setDisplayZeros}
                expandDetail={expandDetail}
                setExpandDetail={setExpandDetail}
                employeeCount={employeeCount}
                setEmployeeCount={setEmployeeCount}
                filteredEmployeeCount={filteredEmployeeCount}
                setFilteredEmployeeCount={setFilteredEmployeeCount}
                showOnlyPending={showOnlyPending}
            />
        </CollapsibleArea>
    );
}

export default PTOAdminTableWrapper;